import { useMemo, useState } from 'react'
import { ProfileFrame } from '../loyalty/AchievementBadge'
import type { AchievementLevel } from '../../types'

interface AvatarProps {
  src?: string | null
  alt?: string
  name?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  // Loyalty frame props
  loyaltyLevel?: AchievementLevel
  showLoyaltyFrame?: boolean
  frameVariant?: 'default' | 'list'
}

const Avatar = ({
  src,
  alt,
  name,
  size = 'md',
  className = '',
  loyaltyLevel,
  showLoyaltyFrame = false,
  frameVariant = 'default'
}: AvatarProps) => {
  // State to handle image loading errors
  const [imageError, setImageError] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-24 h-24 text-2xl'
  }

  // Get the initials from the name for the fallback
  const initial = useMemo(() => {
    if (!name) return '?'

    // Clean the name and get initials
    const cleanName = name.trim()
    if (!cleanName) return '?'

    // Split by spaces and get first letter of each word (max 2)
    const words = cleanName.split(/\s+/).filter(word => word.length > 0)
    if (words.length === 0) return '?'

    if (words.length === 1) {
      // Single word, take first character
      return words[0].charAt(0).toUpperCase()
    }

    // Multiple words, take first letter of first and last word
    const firstInitial = words[0].charAt(0).toUpperCase()
    const lastInitial = words[words.length - 1].charAt(0).toUpperCase()
    return `${firstInitial}${lastInitial}`
  }, [name])

  // Generate a consistent color based on the name
  const backgroundColor = useMemo(() => {
    if (!name) return 'bg-gray-500'

    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-teal-500'
    ]

    let hash = 0
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }

    return colors[Math.abs(hash) % colors.length]
  }, [name])

  // Reset image error state when src changes
  useMemo(() => {
    setImageError(false)
    setImageLoaded(false)
  }, [src])

  // Check if we should show image or fallback
  const shouldShowImage = src && !imageError && src.trim() !== ''

  // Avatar content (image or initial)
  const avatarContent = shouldShowImage ? (
    <img
      src={src}
      alt={alt || name || 'Avatar'}
      className={`${sizeClasses[size]} rounded-xl object-cover`}
      onError={() => setImageError(true)}
      onLoad={() => setImageLoaded(true)}
      style={{
        display: imageLoaded ? 'block' : 'none'
      }}
    />
  ) : null

  // Fallback content
  const fallbackContent = (
    <div
      className={`${sizeClasses[size]} ${backgroundColor} rounded-xl flex items-center justify-center text-white font-semibold`}
    >
      {initial}
    </div>
  )

  // Combined content
  const combinedContent = (
    <div className="relative">
      {/* Always show fallback as base */}
      {fallbackContent}

      {/* Show image on top when loaded successfully */}
      {shouldShowImage && (
        <div className="absolute inset-0">
          {avatarContent}
        </div>
      )}

      {/* Loading placeholder */}
      {shouldShowImage && !imageLoaded && !imageError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-xl">
          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  )

  // Return with or without loyalty frame
  if (showLoyaltyFrame && loyaltyLevel) {
    return (
      <ProfileFrame
        level={loyaltyLevel}
        size={size}
        variant={frameVariant}
        className={className}
      >
        {combinedContent}
      </ProfileFrame>
    )
  }

  return (
    <div className={className}>
      {combinedContent}
    </div>
  )
}

export default Avatar